import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { makeRequest } from "../../utils/request";
import { ItemState } from "./restSlice";

export type AgentEvaluatorAssignment = {
  id: string;
  agentId: string;
  evaluatorId: string;
  samplingRate?: number | null;
  publicId?: string;
  createdAt?: string;
  updatedAt?: string;
};

type ApiAgentEvaluatorAssignment = {
  id?: string | number;
  publicId?: string;
  agentId: string;
  evaluatorId: string;
  samplingRate?: number | null;
  createdAt?: string;
  updatedAt?: string;
};

const normalizeAssignment = (assignment: ApiAgentEvaluatorAssignment): AgentEvaluatorAssignment => {
  const fallbackId = `${assignment.agentId}:${assignment.evaluatorId}`;

  return {
    id: assignment.publicId || (assignment.id ? String(assignment.id) : fallbackId),
    agentId: assignment.agentId,
    evaluatorId: assignment.evaluatorId,
    samplingRate: assignment.samplingRate,
    publicId: assignment.publicId,
    createdAt: assignment.createdAt,
    updatedAt: assignment.updatedAt,
  };
};

const initialState: ItemState<AgentEvaluatorAssignment> = {
  ids: [],
  entities: {},
  isLoading: false,
  isLoadingCreate: false,
  isLoadingDelete: false,
  metadata: {
    total: 0,
    page: 1,
    limit: 100,
  },
};

export const getAgentEvaluatorAssignments = createAsyncThunk("agentEvaluatorAssignment/get", async ({ agentId }: { agentId: string }) => {
  const { data } = await makeRequest<{ data: ApiAgentEvaluatorAssignment[] }>(`/v1/agents/${agentId}/evaluators`);

  return data.map(normalizeAssignment);
});

export const createAgentEvaluatorAssignment = createAsyncThunk(
  "agentEvaluatorAssignment/create",
  async ({ agentId, evaluatorId, samplingRate }: { agentId: string; evaluatorId: string; samplingRate?: number }) => {
    const { data } = await makeRequest<{ data: ApiAgentEvaluatorAssignment }>(`/v1/agents/${agentId}/evaluators`, {
      method: "POST",
      body: {
        evaluatorId,
        ...(samplingRate !== undefined ? { samplingRate } : { samplingRate: null }),
      },
    });

    return normalizeAssignment(data);
  },
);

export const updateAgentEvaluatorAssignment = createAsyncThunk(
  "agentEvaluatorAssignment/update",
  async ({ agentId, evaluatorId, samplingRate }: { agentId: string; evaluatorId: string; samplingRate?: number }) => {
    const { data } = await makeRequest<{ data: ApiAgentEvaluatorAssignment }>(`/v1/agents/${agentId}/evaluators/${evaluatorId}`, {
      method: "PATCH",
      body: {
        ...(samplingRate !== undefined ? { samplingRate } : { samplingRate: null }),
      },
    });

    return normalizeAssignment(data);
  },
);

export const deleteAgentEvaluatorAssignment = createAsyncThunk(
  "agentEvaluatorAssignment/delete",
  async ({ agentId, evaluatorId }: { agentId: string; evaluatorId: string }) => {
    await makeRequest(`/v1/agents/${agentId}/evaluators/${evaluatorId}`, {
      method: "DELETE",
    });

    return { evaluatorId };
  },
);

const agentEvaluatorAssignmentSlice = createSlice({
  name: "agentEvaluatorAssignment",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getAgentEvaluatorAssignments.pending, (state) => {
      state.isLoading = true;
    });

    builder.addCase(getAgentEvaluatorAssignments.fulfilled, (state, action: PayloadAction<AgentEvaluatorAssignment[]>) => {
      state.isLoading = false;
      state.ids = action.payload.map((assignment) => assignment.id);
      state.entities = action.payload.reduce((acc: Record<string, AgentEvaluatorAssignment>, assignment: AgentEvaluatorAssignment) => {
        acc[assignment.id] = assignment;
        return acc;
      }, {});
      state.metadata.total = action.payload.length;
    });

    builder.addCase(getAgentEvaluatorAssignments.rejected, (state) => {
      state.isLoading = false;
    });

    builder.addCase(createAgentEvaluatorAssignment.pending, (state) => {
      state.isLoadingCreate = true;
    });

    builder.addCase(createAgentEvaluatorAssignment.fulfilled, (state, action: PayloadAction<AgentEvaluatorAssignment>) => {
      state.isLoadingCreate = false;
      if (!state.ids.includes(action.payload.id)) {
        state.ids.push(action.payload.id);
      }
      state.entities[action.payload.id] = action.payload;
    });

    builder.addCase(createAgentEvaluatorAssignment.rejected, (state) => {
      state.isLoadingCreate = false;
    });

    builder.addCase(updateAgentEvaluatorAssignment.fulfilled, (state, action: PayloadAction<AgentEvaluatorAssignment>) => {
      state.entities[action.payload.id] = action.payload;
    });

    builder.addCase(deleteAgentEvaluatorAssignment.pending, (state) => {
      state.isLoadingDelete = true;
    });

    builder.addCase(deleteAgentEvaluatorAssignment.fulfilled, (state, action: PayloadAction<{ evaluatorId: string }>) => {
      state.isLoadingDelete = false;
      const assignmentIdToRemove = state.ids.find((id) => state.entities[id]?.evaluatorId === action.payload.evaluatorId);
      if (assignmentIdToRemove) {
        state.ids = state.ids.filter((id) => id !== assignmentIdToRemove);
        delete state.entities[assignmentIdToRemove];
      }
    });

    builder.addCase(deleteAgentEvaluatorAssignment.rejected, (state) => {
      state.isLoadingDelete = false;
    });
  },
});

export const agentEvaluatorAssignmentReducer = agentEvaluatorAssignmentSlice.reducer;
export const agentEvaluatorAssignmentActions = {
  getAssignments: getAgentEvaluatorAssignments,
  createAssignment: createAgentEvaluatorAssignment,
  updateAssignment: updateAgentEvaluatorAssignment,
  deleteAssignment: deleteAgentEvaluatorAssignment,
};
